/*
 * The FAQ list (components/sections/FaqAccordion.tsx), for every
 * `custom_faq` list marked data-faq: the FAQ page (faq-list) and the
 * questions on About (about-story). The product page's list is run by
 * product.js and stops its clicks here, so it is left alone.
 *
 * One answer open at a time per list, exactly as the component's
 * `open === i` state: a click on the open question closes it, a click on
 * another moves the open row. The row gets is-open, the button
 * aria-expanded and the panel its hidden attribute.
 *
 * The FAQ page sends every answer open in its HTML (so crawlers and
 * people without JavaScript read them) with data-faq-button on the
 * questions; the other lists use data-faq-toggle. Setup closes every
 * answer that is not marked open, so both start folded.
 */

const TOGGLE = "[data-faq-toggle], [data-faq-button]";

const BOUND = new WeakSet();

function setupFaq(list) {
  if (BOUND.has(list)) return;
  BOUND.add(list);

  const items = Array.from(list.querySelectorAll(".custom-faq-item"));

  function setOpen(index) {
    items.forEach((item, i) => {
      const button = item.querySelector(TOGGLE);
      const panel = item.querySelector(".custom-faq-text");
      const isOpen = i === index;
      item.classList.toggle("is-open", isOpen);
      if (button) button.setAttribute("aria-expanded", isOpen ? "true" : "false");
      if (panel) panel.hidden = !isOpen;
    });
  }

  setOpen(items.findIndex((item) => item.classList.contains("is-open")));

  items.forEach((item, i) => {
    const button = item.querySelector(TOGGLE);
    if (!button) return;
    button.addEventListener("click", () => {
      const isOpen = button.getAttribute("aria-expanded") === "true";
      setOpen(isOpen ? null : i);
    });
  });
}

function init(root = document) {
  root.querySelectorAll("[data-faq]").forEach(setupFaq);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => init(), { once: true });
} else {
  init();
}

// Theme editor: a section re-rendered in place needs its behaviour again.
document.addEventListener("shopify:section:load", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (target) init(target);
});

export { setupFaq };
